import { BasemapProvider } from 'features/builder'
import { FieldToOrderBy, ResourceType, SortDirection } from 'features/common'
import { ProviderType } from 'features/connections'
import { UserRole } from 'features/users/types'

export interface AccountStatsDetails {
  // from workspace-api/account/stats
  apps: number
  map_loads: number
  all_users_maps_count: number
  all_users_workflows_count: number
  users: number
  geocodings: number
  isolines: number
  tokenCount: number

  // from accounts-api/account/stats
  editorUsers: number
  viewerUsers: number
  privateApps: number
  superadminUsers: number

  totalMaps: number
  publicMaps: number

  totalWorkflows: number
  usageQuota: number
  lds: number
  basemaps: number

  builderGenAiThreads: number
  builderGenAiThreadsQuota: number
}

export interface AccountStats {
  account_id: string
  stats: AccountStatsDetails
}

export interface AccountLimits {
  shareMapsAvailable: boolean
  carto_dw_computing_limit: number
  max_editor_users: number
  map_loads_quota: number
  max_viewer_users: number
  max_connections: number
  geocoding_quota: number
  geocodingQuota: number
  isolinesQuota: number
  enterpriseSsoAvailable: boolean
  enterpriseSsoGroupsAvailable: boolean
  mfa_available: boolean
  maps_quota: number
  mapsPublicQuota: number
  max_apps: number
  max_rows_per_table_layer: number
  max_rows_per_sql_layer: number
  max_size_per_table_layer: number
  max_size_per_sql_layer: number
  max_layers: number
  doPremiumSubscriptionsAvailable: boolean
  doPublicSubscriptionsAvailable: boolean
  doSamplesAvailable: boolean
  workflows_quota: number
  maxTokens: number
  usageQuota: number
  usageQuotaResetDay: string
  ldsQuota: number
  maxCustomBasemaps: number
}

// TODO VVG Consider moving this to a separate file (class)
export enum AccountPlan {
  Trial = 'TRIAL',
  Grant = 'GRANT',
  Student = 'STUDENT',
  Starter = 'STARTER',
  StarterAutorenew = 'STARTER-AUTORENEW',
  EZero = 'EZERO',
  ESmall = 'ESMALL',
  EMedium = 'EMEDIUM',
  ELarge = 'ELARGE',
  EStrategic = 'ESTRATEGIC',
  Individual = 'INDIVIDUAL',
  Free = 'FREE',
  Beta = 'BETA',
  Ambassador = 'AMBASSADOR',
  FreeFromC2 = 'FREE-FROM-C2'
}

export function isAutoRenewablePlan(plan: AccountPlan) {
  return [AccountPlan.Individual, AccountPlan.StarterAutorenew].includes(plan)
}

export function isEnterprisePlan(plan: AccountPlan) {
  return (
    plan === AccountPlan.EZero ||
    plan === AccountPlan.ESmall ||
    plan === AccountPlan.EMedium ||
    plan === AccountPlan.ELarge ||
    plan === AccountPlan.EStrategic ||
    plan === AccountPlan.Starter
  )
}

export const AccountPlanLabels = {
  [AccountPlan.Trial]: 'Trial',
  [AccountPlan.Grant]: 'Grant',
  [AccountPlan.Student]: 'Student',
  [AccountPlan.Starter]: 'Starter',
  [AccountPlan.StarterAutorenew]: 'Starter',
  [AccountPlan.EZero]: 'Enterprise',
  [AccountPlan.ESmall]: 'Enterprise S',
  [AccountPlan.EMedium]: 'Enterprise M',
  [AccountPlan.ELarge]: 'Enterprise L',
  [AccountPlan.EStrategic]: 'Enterprise Strategic',
  [AccountPlan.Individual]: 'Individual',
  [AccountPlan.Free]: 'Free',
  [AccountPlan.Beta]: 'Beta',
  [AccountPlan.Ambassador]: 'Ambassador',
  [AccountPlan.FreeFromC2]: 'Free'
}

export interface Tenant {
  domain: string
  installation: TenantInstallation
  name: string
}

export enum RoleMappingMethod {
  User = 'user',
  Group = 'group'
}

export interface Account {
  id: string
  name: string
  tenant_domain: string
  tenant_name: string
  expirationDate: string
  createdWhen: string
  on_trial: boolean
  organizationId: string
  plan: {
    name: AccountPlan
  }
  email_domain: string
  requires_join_approval: boolean
  spatial_extension_project: string
  do_project_id: string
  quotas: AccountLimits
  suspended: boolean
  days_after_expiration: number
  billingInformation?: string
  businessVerticals?: string[]
  enterpriseBrandingAvailable?: boolean
  exportViewportDataAvailable?: boolean
  exportPdfReportAvailable?: boolean
  builderCustomCodeAvailable?: boolean
  activityDataAvailable?: boolean
  tenant: Tenant
  usageBasedPricing?: boolean
  usageQuota: number
  usageQuotaResetDay: string
  defaultUserRole?: UserRole
  ssoJitEnabled?: boolean
  suppressNps?: boolean
  hideCartoDatawarehouse?: boolean
  partnerConnect?: string
  roleMappingMethod?: RoleMappingMethod
  documentModeAvailable?: boolean
  enforcedLoginMethod?: 'sso' | null
  isLoginMethodEnforcementAvailable?: boolean
}

export interface Users {
  app_metadata: {
    roles: UserRole[]
  }
  created_at: string
  email: string
  email_verified: boolean
  identities: {
    connection: string
    isSocial: boolean
    provider: string
    user_id: string
    access_token: string
    expires_in: number
  }[]
  last_ip: string
  last_login: string
  logins_count: number
  name: string
  nickname: string
  picture: string
  updated_at: string
  user_id: string
  user_metadata: {
    account_id: string
    account_name: string
    tenant_domain: string
    tenant_id?: string
    is_carto_user?: boolean
  }
}

export interface UsersListFilter {
  searchBy?: string
  role?: UserRole
}

export interface LdsQuotas {
  annual_quota: number
  monthly_quota?: number
  providers: {
    geocoding: string
    isolines: string
    routing: string
  }
  used_quota: number
}

export enum JoinMethod {
  Invitation = 'Invitation',
  Signup = 'Signup',
  Backoffice = 'Backoffice',
  Join = 'Join',
  Request = 'Request'
}

export enum InvitationStatus {
  Pending = 'pending',
  Sent = 'sent',
  Approved = 'approved',
  Requested = 'requested'
}

export interface Invitation {
  joinMethod: JoinMethod
  recipient: string
  token: string
  status?: InvitationStatus
  requestedAt?: string
  targetRole?: UserRole
  expired: boolean
}

export enum Feature {
  Sso = 'sso',
  Groups = 'groups',
  Appearance = 'appearance',
  ActivityData = 'activityData'
}

export enum TenantInstallation {
  Saas = 'saas',
  Onprem = 'onprem'
}

export type BasemapSettings = Partial<Record<BasemapProvider, boolean>>

export type ColorRange = {
  name: string
  type: string
  colors: string[]
  category: string
}

export type Palette = {
  colorRange: ColorRange
}

export type PalettesSettings = {
  palettes: Palette[]
}

interface BaseAccountResource {
  id: string
  resourceType: ResourceType
  name: string
  privacy: string
  accountId: string
  userId: string
  userEmail: string
  createdAt: string
  updatedAt: string
  mapCount?: number
  workflowCount?: number
}

interface ConnectionAccountResource extends BaseAccountResource {
  resourceType: ResourceType.Connection
  providerId: ProviderType
  connectionType: string
}

export type AccountResource = BaseAccountResource | ConnectionAccountResource

export interface ResourceListFilters {
  resourceType: ResourceType
  page: number
  pageSize: number
  q?: string
  orderBy: FieldToOrderBy
  orderDirection: SortDirection
  ownerBy?: Users[]
}

export interface ResourceDelete {
  id: string
  resourceType: ResourceType
}

export type ResourcesDeleteRequest = ResourceDelete[]

export interface ResourceDeleteResponse extends ResourceDelete {
  success: boolean
  failureReason?: string
}

export type ResourcesDeleteResponse = ResourceDeleteResponse[]

export interface ResourceTransfer {
  receiverUserId: string
  resources: ResourceDelete[]
}

export type ResourcesTransferRequest = ResourceTransfer

export interface ResourceTransferResponse extends ResourceTransfer {
  success: boolean
  failureReason?: string
}

export type ResourcesTransferResponse = ResourceTransferResponse[]

export interface WorkflowsSettings {
  installExtensions: boolean
}

export interface AccountAgentSettings {
  enabled: boolean
}

export interface SupportAccessStatus {
  createdAt: string
  expirationDate: string
  grantedByUserEmail: string
}

export interface Subscription {
  subscriptionType: string
  datasetType: string
  datasetId: string
  expiresAt: string
  implicit: boolean
  tableName: string
  availableIn: AvailableIn[]
}
export interface AvailableIn {
  provider: ProviderType
  regions: string[]
}

export type DataSubscriptionsResponse = Subscription[]

export interface CatalogDataset {
  id: string
  slug: string
  name: string
  description: string
  createdAt: string
  isGeography: boolean
  categoryId: string
  categoryName: string
  countryId: string
  countryName: string
  providerId: string
  providerName: string
  providerDescription: string
  providerWebsite: string
  licenseId: string
  licenseName: string
  licensesLink: string
  documentationLink: string
  updateFrequency: string | null | string[]
  spatialAggregationId: string
  spatialAggregationName: string
  placetypeId: string
  placetypeName: string
  thumbnail: string | null
  sampleInfo?: {
    id: string
    source?: string
  }
  useCases?: Record<string, unknown> // schema not validated here
  version: string
  geographyId: string | null
  geographySlug: string | null
  geographyName: string | null
  temporalAggregation: string
  rank: number
}

export interface CatalogDatasetsResponse {
  results: CatalogDataset[]
  totalResults: number
}

export interface AccountBillingInformation {
  settingsUrl?: string
}

// Account Governance
export type ProviderName =
  | 'bigquery'
  | 'redshift'
  | 'postgres'
  | 'snowflake'
  | 'databricks'
  | 'databricksWarehouse'
  | 'oracle'
export type ConnectionType =
  | 'oauth'
  | 'serviceAccount'
  | 'workloadIdentityFederation'
  | 'usernamePassword'
  | 'keyPair'
  | 'personalAccessToken'
  | 'oauthU2M'
  | 'oauthM2M'
  | 'wallet'

export type ProvidersState = {
  [key in ProviderName]?: false | { [type in ConnectionType]?: boolean }
}

export interface AccountGovernanceConnectionsData {
  providers: ProvidersState
  cartoDataWarehouse: boolean
  allowEditorsToCreate: boolean
  allowSharing: boolean
}

export interface AccountGovernanceMapsData {
  allowUsersToCreatePublicMaps: boolean
  allowUsersToManagePDFReports: boolean
  allowUsersToManageViewportDataExport: boolean
}
